/**
 * AppStyleEditorDialogs — Phase 2.2 (Plan §8) App.tsx slice #1.
 *
 * First incremental slice of the App.tsx (5728 LOC) decomposition.
 * Extracts the four style-editor dialog mounts (DIMSTYLE / MLEADERSTYLE
 * / STYLE / Font Manager) into a single sub-component so App.tsx no
 * longer renders 23 LOC of repeated `<Suspense fallback={null}>` /
 * `{...hook.props}` boilerplate inline. Each dialog stays lazy-loaded
 * (see `App.tsx` lazy imports); this component only swaps the mount
 * boilerplate, not the lazy chunks.
 *
 * Pattern: the parent passes the four hook results directly; this
 * component does NOT call hooks itself (it would re-call `useState`
 * on different render order). Hook ownership stays with `App.tsx`.
 */

import { Suspense } from "react";

import type { UseDimStyleEditorDialogResult } from "../hooks/useDimStyleEditorDialog";
import type { UseFontManagerDialogResult } from "../hooks/useFontManagerDialog";
import type { UseMLeaderStyleEditorDialogResult } from "../hooks/useMLeaderStyleEditorDialog";
import type { UseTextStyleEditorDialogResult } from "../hooks/useTextStyleEditorDialog";

interface AppStyleEditorDialogsProps {
  dimStyleEditor: UseDimStyleEditorDialogResult;
  mleaderStyleEditor: UseMLeaderStyleEditorDialogResult;
  textStyleEditor: UseTextStyleEditorDialogResult;
  fontManager: UseFontManagerDialogResult;
  DimStyleEditorDialog: React.ComponentType<
    UseDimStyleEditorDialogResult["props"] & object
  >;
  MultileaderStyleEditorDialog: React.ComponentType<
    UseMLeaderStyleEditorDialogResult["props"] & object
  >;
  TextStyleEditorDialog: React.ComponentType<
    UseTextStyleEditorDialogResult["props"] & object
  >;
  FontManagerDialog: React.ComponentType<
    UseFontManagerDialogResult["props"] & object
  >;
}

export function AppStyleEditorDialogs({
  dimStyleEditor,
  mleaderStyleEditor,
  textStyleEditor,
  fontManager,
  DimStyleEditorDialog,
  MultileaderStyleEditorDialog,
  TextStyleEditorDialog,
  FontManagerDialog,
}: AppStyleEditorDialogsProps): JSX.Element {
  return (
    <>
      {dimStyleEditor.isOpen && dimStyleEditor.props ? (
        <Suspense fallback={null}>
          <DimStyleEditorDialog {...dimStyleEditor.props} />
        </Suspense>
      ) : null}

      {mleaderStyleEditor.isOpen && mleaderStyleEditor.props ? (
        <Suspense fallback={null}>
          <MultileaderStyleEditorDialog {...mleaderStyleEditor.props} />
        </Suspense>
      ) : null}

      {textStyleEditor.isOpen ? (
        <Suspense fallback={null}>
          <TextStyleEditorDialog {...textStyleEditor.props} />
        </Suspense>
      ) : null}

      {fontManager.isOpen ? (
        <Suspense fallback={null}>
          <FontManagerDialog {...fontManager.props} />
        </Suspense>
      ) : null}
    </>
  );
}
